import { useCallback, useMemo } from 'react';
import { useMaybeAudio } from '@/audio/AudioProvider';
import { useAudioSettings } from '@/stores/audio-settings';
import { haptic } from './haptics';

/**
 * The sound-and-haptic moments from plan doc 15, as one hook. Components call
 * `sfx.correct()` and the choreography's sound and buzz happen together —
 * pairing them here is what stops the two drifting apart as screens are added.
 *
 * The audio context is read optionally. A button that makes a click is used in
 * Storybook and in unit tests, where there is no provider, and a missing sound
 * is not worth a thrown error.
 */
export function useSfx() {
  const audio = useMaybeAudio();
  const ui = audio?.ui;
  const hapticsOn = useAudioSettings((s) => s.haptics);

  const tap = useCallback(() => {
    ui?.('tap');
    haptic('light', hapticsOn);
  }, [ui, hapticsOn]);

  return useMemo(
    () => ({
      tap,
      pop: (index = 0) => {
        ui?.('pop', { index });
        haptic('light', hapticsOn);
      },
      correct: (rootMidi?: number, minor?: boolean) => {
        ui?.('correct', {
          ...(rootMidi !== undefined ? { rootMidi } : {}),
          ...(minor !== undefined ? { minor } : {}),
        });
        haptic('success', hapticsOn);
      },
      wrong: () => {
        ui?.('wrong');
        haptic('medium', hapticsOn);
      },
      combo: (count: number, rootMidi?: number) => {
        ui?.('combo', { index: count, ...(rootMidi !== undefined ? { rootMidi } : {}) });
        haptic(count >= 10 ? 'heavy' : 'light', hapticsOn);
      },
      xpTick: (index: number) => ui?.('xpTick', { index }),
      levelUp: () => {
        ui?.('levelUp');
        haptic('double', hapticsOn);
      },
      streak: () => {
        ui?.('streak');
        haptic('double', hapticsOn);
      },
      /** Moving up a league. Demotion is Tempo's line and a nudge, not a sound. */
      promote: () => {
        ui?.('promote');
        haptic('double', hapticsOn);
      },
      unlock: () => ui?.('unlock'),
      chest: () => ui?.('chest'),
      /** Page and sheet transitions. Deliberately very quiet. */
      whoosh: () => ui?.('whoosh'),
      /**
       * The tuner settling on the note: the `correct` sound as a single tone at
       * the string's own pitch, so it reads as "that note" rather than "a ding".
       */
      inTune: (rootMidi: number) => {
        ui?.('correct', { rootMidi });
        haptic('light', hapticsOn);
      },
      /** Opt-in, and only on the accented beat, or a bar feels like a rattle. */
      metronomeAccent: () => haptic('light', hapticsOn),
    }),
    [ui, tap, hapticsOn],
  );
}
